import * as lodash from 'lodash';
import { ColorCodeConverter } from '../../../../utilities/colorcode-converter';
import { UiManager } from '../../ui/ui.manager';
import { TextController } from './text-controller';
import { CjContainer, CjShape, CjText } from '../../../model/cj-factory';
import { ImageEvent } from '../image/image-event';
import { CjShadow } from '../../../model/cj-factory/cj-shadow';
import { FontSizeList } from '../../../model/font-size-list';
import { CJ_CONTAINER_NAME, CJ_FOCUS_NAME } from '../../../model/cj-factory/cj-obj-name.collection';
import { CjTool } from '../../../../utilities/cj-tool';
import { EditableEvent } from '../editable-event';
import { PosManager } from '../../pos/pos.manager';
import { cloneDeep } from 'lodash';
import { LayoutDataManager } from '../../data/layout-data.manager';
import { SmartGuideManager } from '../../smart-guide/smart-guide.manager';
import { store } from '../../../../app/store';

export class TextEvent {
  private stage: CjContainer;
  private readonly pageId: string;


  private textController!: TextController;
  private imageEvent!: ImageEvent;
  private editableEvent!: EditableEvent;
  private posManager!: PosManager;
  private layoutDataManager!: LayoutDataManager;
  private smartGuideManager!: SmartGuideManager;

  isFocus: boolean = false;
  isActive: boolean = false;
  isInsert: boolean = false;
  targetFrame: CjContainer | null = null;

  private clicked: boolean = false;
  private dragPointX: number = 0;
  private dragPointY: number = 0;

  constructor(stage: CjContainer, pageId: string) {
    this.stage = stage;
    this.pageId = pageId;
  }

  di(
    textController: TextController,
    imageEvent: ImageEvent,
    editableEvent: EditableEvent,
    posMng: PosManager,
    layoutDataMng: LayoutDataManager,
    smartGuideMng: SmartGuideManager,
  ): void {
    this.textController = textController;
    this.imageEvent = imageEvent;
    this.editableEvent = editableEvent;
    this.posManager = posMng;
    this.layoutDataManager = layoutDataMng;
    this.smartGuideManager = smartGuideMng;
  }

  initialize(): void {
  }

  destroy(): void {
    this.isFocus = false;
    this.targetFrame = null;
    this.clicked = false;
    this.dragPointX = 0;
    this.dragPointY = 0;
  }

  update(stage: CjContainer): void {
    this.stage = stage;
  }

  /**
   * テキストの実際の描画内容（グリフ）に基づいてピクセルベースのバウンディングボックスを計測する。
   * Canvas に文字列を描画し、不透明ピクセルから文字の実表示領域を計算。
   *
   * @param bounds - 元のバウンディングボックス（位置基準用）
   * @param textStr - 描画するテキスト文字列
   * @param font - 使用するフォント名（例: "MS Gothic"）
   * @param fontSize - フォントサイズ（例: "30px"）
   * @returns 実際の描画ピクセルに基づいた x/y/width/height の補正後バウンディングボックス
   */
  measureActualGlyphBox(bounds: createjs.Rectangle, textStr?: string, font?: string, fontSize?: string) {
    // 引数が不足している場合は元の bounds をそのまま返す
    if (!textStr || !font || !fontSize) return bounds;

    // オフスクリーン canvas を作成
    const canvas = document.createElement("canvas");
    canvas.width = 500;
    canvas.height = 200;
    const ctx = canvas.getContext("2d");
    if (!ctx) return bounds;

    // フォント設定と描画スタイル
    ctx.font = `${fontSize} ${font}`;
    ctx.textBaseline = "top";  // 上端を基準に描画 → (0, 0) に配置しやすくする
    ctx.fillStyle = "#000";
    ctx.fillText(textStr, 0, 0); // 実際の文字を描画

    // 描画結果を取得（RGBA ピクセル配列）
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

    // グリフの上下左右端を初期化（極値）
    let top = canvas.height, bottom = 0;
    let left = canvas.width, right = 0;

    // ピクセル単位で alpha 値をスキャン → 描画されている範囲を探索
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const alpha = imageData[(y * canvas.width + x) * 4 + 3]; // アルファチャンネル（不透明度）
        if (alpha > 0) {
          if (y < top) top = y;
          if (y > bottom) bottom = y;
          if (x < left) left = x;
          if (x > right) right = x;
        }
      }
    }

    // 元の bounds に対して相対位置で補正した新しい矩形を返す
    return {
      x: bounds.x + left,
      y: bounds.y + top,
      width: right - left + 1,    // +1 はピクセル範囲を inclusive にするため
      height: bottom - top + 1
    };
  }

  setFocus(container: CjContainer, isRestore: boolean, callback?: () => void) {
    if (this.targetFrame || this.imageEvent.targetFrame || !isRestore) {
      this.editableEvent.disFocused(this.targetFrame!);
      this.imageEvent.targetFrame && this.editableEvent.disFocused(this.imageEvent.targetFrame);
    }
    if (!isRestore) {
      this.isFocus = true;
      this.targetFrame = container;
    }
    CjTool.checkLoad(container).then((v) => {
      const data = v.getTransformedBounds();
      // const _data = v.getTransformedBounds();
      // const data = this.measureActualGlyphBox(_data, container.text, container.fontName, container.textSize);

      const focusContainer = new CjContainer();
      focusContainer.name = CJ_CONTAINER_NAME.focus.text;
      const focus: CjShape = new CjShape();
      focus.name = CJ_FOCUS_NAME.line;
      focusContainer.addChild(focus);
      focus.graphics.s('#A5B9F4');
      focus.graphics.sd([10, 5], 0);
      // focus.graphics.beginStroke('#A5B9F4');
      // focus.graphics.setStrokeStyle(2);
      focus.graphics.dr(data.x - 10 - container.x, data.y - 10 - container.y, data.width + 20, data.height + 20);
      const corner: CjShape = new CjShape();
      corner.graphics.f('#A5B9F4').dc(data.x - 10 + data.width + 20 - container.x, data.y - 10 - container.y, 5);
      corner.graphics.f('#A5B9F4').dc(data.x - 10 + data.width + 20 - container.x, data.y - 10 + data.height + 20 - container.y, 5);
      corner.graphics.f('#A5B9F4').dc(data.x - 10 - container.x, data.y - 10 + data.height + 20 - container.y, 5);
      corner.graphics.f('#A5B9F4').dc(data.x - 10 - container.x, data.y - 10 - container.y, 5);
      corner.name = CJ_FOCUS_NAME.corner;
      focusContainer.addChild(corner);
      focusContainer.visible = !isRestore;
      v.addChild(focusContainer);
      UiManager.ins.on('r->l:shift-move:text', (v) => {
        if (!this.isFocus) return;
        if (store.getState().dialog.dialogs.length) return;
        this.editableEvent.action(
          'shift-move:text',
          `${v.x} ${v.y}移動`,
          () => {
            container.x += v.x;
            container.y += v.y;
          },
          () => {
            container.x -= v.x;
            container.y -= v.y;
          },
        );
      });
      callback?.();
    });
  }

  showFocus(container: CjContainer) {
    if (this.targetFrame || this.imageEvent.targetFrame) {
      this.editableEvent.disFocused(this.targetFrame!);
      this.imageEvent.targetFrame && this.editableEvent.disFocused(this.imageEvent.targetFrame);
    }
    this.updateFocus(container);
    this.isFocus = true;
    this.targetFrame = container;
    const textFocus = CjTool.getTextFocus(container);
    textFocus.visible = true;
    UiManager.ins.on('r->l:shift-move:text', (v) => {
      if (!this.isFocus) return;
      if (store.getState().dialog.dialogs.length) return;
      this.editableEvent.action(
        'shift-move:text',
        `${v.x} ${v.y}移動`,
        () => {
          container.x += v.x;
          container.y += v.y;
        },
        () => {
          container.x -= v.x;
          container.y -= v.y;
        },
      );
    });
  }

  updateFocus(container: CjContainer) {
    this.isFocus = true;
    this.targetFrame = container;
    const textFocus = CjTool.getTextFocus(container);
    const line = textFocus.getChildByName(CJ_FOCUS_NAME.line) as CjShape;
    const corner = textFocus.getChildByName(CJ_FOCUS_NAME.corner) as CjShape;
    line.graphics.clear();
    corner.graphics.clear();
    const data = container.getTransformedBounds();
    // const _data = container.getTransformedBounds();
    // const data = this.measureActualGlyphBox(_data, container.text, container.fontName, container.textSize);

    line.graphics.s('#A5B9F4');
    line.graphics.sd([10, 5], 0);
    line.graphics.dr(data.x - 10 - container.x, data.y - 10 - container.y, data.width + 20, data.height + 20);
    corner.graphics.f('#A5B9F4').dc(data.x - 10 + data.width + 20 - container.x, data.y - 10 - container.y, 5);
    corner.graphics.f('#A5B9F4').dc(data.x - 10 + data.width + 20 - container.x, data.y - 10 + data.height + 20 - container.y, 5);
    corner.graphics.f('#A5B9F4').dc(data.x - 10 - container.x, data.y - 10 + data.height + 20 - container.y, 5);
    corner.graphics.f('#A5B9F4').dc(data.x - 10 - container.x, data.y - 10 - container.y, 5);
    textFocus.rotation = -container.rotation;
    this.stage.stage.update();
  }

  textEditor(text: CjText, container: CjContainer) {
    // this.textController.textImage = text;
    // this.textController.backupText = lodash.cloneDeep(text);

    let textShadow: CjShadow;
    let shadowColor: string = '#000000';
    let shadowOffset: number = 10;
    let shadowAlpha: number = 0;
    if (text.shadow) {
      textShadow = text.shadow;
      shadowColor = textShadow.color ?? '#000000';
      shadowOffset = textShadow.offsetX ?? 10;
      shadowAlpha = ColorCodeConverter.rgbaToColorCode(shadowColor ?? 'rgba(0,0,0,0.65)').alpha;
    }
    // const fontLabel = text.fontData?.size ?? '24pt';
    const fontLabel = text.fontData?.size.replace('px', 'pt') ?? '24pt';
    const fontSize = FontSizeList.find((v) => v.label === fontLabel);

    const textBounds = container.getTransformedBounds();
    const pos = this.posManager.canvasToBrowser({
      x: container.x,
      y: container.y
    });
    const adjustedPos = this.posManager.canvasToBrowser({
      x: container.x,
      y: container.y + textBounds.height + 10 // 10 ... 微調整用
    });
    UiManager.ins.emit('l->r:layout-menu-disabled', { isDisabled: true });
    UiManager.ins.emit('l->r: post-text-pos', { pos });
    this.textController.backupText = cloneDeep(text);
    this.textController.backupTextContainer = cloneDeep(container);
    // --- テキストツール表示 ---
    UiManager.ins.emit('l->r:show-text-tool', {
      isOpen: true,
      textInfo: {
        id: text.textId ?? '',
        isBold: !!text.fontData?.isBold ?? false,
        isItalic: !!text.fontData?.isItalic ?? false,
        textColor: text.color,
        isOnDropper: false,
        fontType: text.options?.fontType ?? 'server',
        font: text.fontData?.family ?? '',
        // fontSize: text.fontData?.size.replace('pt', '') ?? '11',
        fontSize: text.fontData?.size.replace('px', '') ?? '11',
        textTransparency: Math.floor(Number(text.alpha) * 100) ?? 100,
        dropShadow: text.options?.isShadow ?? false,
        transparency: shadowAlpha ? Math.floor(Number(shadowAlpha) * 100) : 65,
        distance: shadowOffset,
        outlineWidth: text.options?.outline ?? 0,
        outlineColor: text.options?.outlineColor ?? '#000000',
        textAlign: this.textController.textAlignFormat(text.textAlign) ?? '0',
        textRotation: text.parent.rotation,
        textSelectRotation: text.parent.rotation,
        lineSpacingNum: Math.floor(Number(text.lineHeight) - text.getMeasuredLineHeight()),
        textAreaContents: text.text ?? '',
        height: textBounds.height,
        pos,
        adjustedPos,
      },
    });
  }

  handleDown(container: CjContainer, e: any) {
    if (container !== this.targetFrame) return;
    this.dragPointX = this.stage.stage.mouseX - container.x;
    this.dragPointY = this.stage.stage.mouseY - container.y;
  }

  // -- containerの移動 --
  handleMove(container: CjContainer, e: any) {
    if (container !== this.targetFrame) return;
    if (!this.isFocus) return;
    // document.body.style.cursor = 'move';
    const { offsetX, offsetY } = this.smartGuideManager.calculateGuideSnap({ container, type: 'text' });
    const baseX = this.stage.stage.mouseX - this.dragPointX;
    const baseY = this.stage.stage.mouseY - this.dragPointY;
    container.x = this.smartGuideManager.applySnap(container.x, baseX, offsetX);
    container.y = this.smartGuideManager.applySnap(container.y, baseY, offsetY);
  }

  handleUp(container: CjContainer, e: any) {
    this.smartGuideManager.clearGuideCache();
    if (container !== this.targetFrame) return;
    if (!this.isFocus) return;
  }

  // - textへのイベント設定 -
  setTextEvent(text: CjText, container: CjContainer) {
    text.on('click', (e: any) => {
      e.stopPropagation();
      if (this.isInsert) return;
      const frameLength = this.stage.numChildren;
      container && this.stage.setChildIndex(container, frameLength - 1);
      // --- ダブルクリックの場合 ---
      if (this.clicked) {
        this.showFocus(container);
        this.clicked = false;
        this.textEditor(text, container);
        return;
      }
      // --- シングルクリックの場合 ---
      this.clicked = true;
      // --- ダブルクリック受付時間 ---
      setTimeout(() => {
        // ---- ダブルクリックだとfalse ----
        if (this.clicked) {
          this.showFocus(container);
        }
        this.clicked = false;
      }, 250);
    });
    text.on('mousedown', (e) => {
      this.handleDown(container, e);
    });
    text.on('pressmove', (e) => {
      this.handleMove(container, e);
    });
    text.on('pressup', (e) => {
      this.handleUp(container, e);
    });
  }
}
